import React, {FC, useEffect, useState} from 'react';

import {Colors, Text} from '@optimaxdev/design-system';
import {ArrowDown, Icon} from '@optimaxdev/icons';
import classNames from 'classnames';
import AnimateHeight from 'react-animate-height';

import {PURCHASE_STORAGE} from 'constants/analytics';
import {PAYMENT_METHODS_EXPRESS} from 'constants/paymentMethods';
import {sessionStore} from 'libs/storage/storage';

import {OrderDetailsAddressType, OrderDetailsPaymentType} from 'types/orderDetailsTypes';

import {ShippingAddress} from '../contacts/shippingAddress/shippingAddress';
import {PaymentMethod} from '../paymentMethod/paymentMethod';

import {
    setChatForTYP,
    getCommonTextProps,
    getCommonTitleProps,
    Wrapper,
    handleAnalyticsOnChatOpen,
    handleAnalyticsOnDropdownToggle,
} from './helpers';

import s from './successAddressInfo.scss';

export type PropsType = {
    shippingAddress: OrderDetailsAddressType;
    billingAddress: OrderDetailsAddressType;
    isSameAddresses: boolean;
    shippingMethod: string;
    payment: OrderDetailsPaymentType;
    paymentMethod?: string;
    hasClickedSeeDetailRef?: React.MutableRefObject<boolean>;
    sendAnalytics: (label: string, isInteraction?: boolean) => void;
};

/**
 * Component for TYP redesign order details shipping & billing address
 * @returns {JSX.Element} - component
 */
export const SuccessAddressInfo: FC<PropsType> = ({
    billingAddress,
    hasClickedSeeDetailRef,
    isSameAddresses,
    payment,
    paymentMethod = '',
    sendAnalytics,
    shippingAddress,
    shippingMethod,
}): JSX.Element => {
    const [isOpened, setIsOpened] = useState(false);
    const placeOfInteraction = sessionStore.get(PURCHASE_STORAGE);
    const isDesktop = __BUILD_TYPE__ === 'desktop';

    const handleToggleCollapse = () => {
        handleAnalyticsOnDropdownToggle();
        sendAnalytics(isDesktop ? 'Shipping info' : `${isOpened ? 'Collapse' : 'Expand'}`, true);
        setIsOpened(v => !v);
    };

    const handleOpenChat = () => {
        handleAnalyticsOnChatOpen(Boolean(hasClickedSeeDetailRef?.current));
        sendAnalytics('Open Chat - Clicked', true);
        setChatForTYP();
    };

    useEffect(() => {
        if (placeOfInteraction === 'Cart' && PAYMENT_METHODS_EXPRESS.includes(paymentMethod)) {
            setIsOpened(true);
        }
    }, [paymentMethod, placeOfInteraction]);

    return (
        <Wrapper
            className={classNames({[s.card]: !isDesktop, [s.desktopCard]: isDesktop})}
            isDesktop={isDesktop}
        >
            <button
                className={classNames(s.button, {[s.desktop]: isDesktop})}
                onClick={handleToggleCollapse}
                type="button"
            >
                <Text
                    color={isDesktop ? Colors.limedSpruce : Colors.mineShaft}
                    lineHeight={isDesktop ? '24px' : '28px'}
                    size={isDesktop ? '16px' : '14px'}
                >
                    Shipping & Billing Info
                </Text>
                <Icon
                    className={classNames({
                        [s.iconOpened]: isOpened,
                    })}
                    color={Colors.gray}
                    icon={ArrowDown}
                    strokeWidth={3}
                    width={10}
                />
            </button>
            <AnimateHeight
                aria-hidden={isOpened}
                className={classNames({[s.container]: isDesktop})}
                data-testid="animate-height-container"
                height={isOpened ? 'auto' : 0}
            >
                <div className={classNames(s.shipping, {[s.desktop]: isDesktop})}>
                    <ShippingAddress
                        isDesktop={isDesktop}
                        isSameAddresses={isSameAddresses}
                        shippingAddress={shippingAddress}
                        shippingMethod={shippingMethod}
                    />
                </div>
                {!isSameAddresses && (
                    <div className={s.block}>
                        <Text {...getCommonTitleProps(isDesktop)}>Billing Address</Text>
                        <Text {...getCommonTextProps(isDesktop)}>
                            {billingAddress.firstname} {billingAddress.lastname}
                        </Text>
                        <Text {...getCommonTextProps(isDesktop)}>
                            {billingAddress.street}, {billingAddress.city},
                        </Text>
                        <Text {...getCommonTextProps(isDesktop)}>
                            {billingAddress.region}, {billingAddress.postcode},{' '}
                            {billingAddress.country_full}
                        </Text>
                        <Text {...getCommonTextProps(isDesktop)}>{billingAddress.telephone}</Text>
                    </div>
                )}
                <div className={s.payment}>
                    <Text {...getCommonTitleProps(isDesktop)}>Payment Method:</Text>
                    <PaymentMethod payment={payment} />
                </div>
                <button
                    className={classNames(s.chatBtn, {[s.desktop]: isDesktop})}
                    onClick={handleOpenChat}
                    type="button"
                >
                    Spotted an error? <span className={s.chatBtnText}>Let&apos;s chat</span>
                </button>
            </AnimateHeight>
        </Wrapper>
    );
};
